import React, { useEffect, useState } from "react";
import { Shell } from "@/components/Shell";
import { api } from "@/lib/api";
import { useAuth } from "@/auth/AuthContext";
import { MessageThread } from "@/components/Messaging";
import { imageUrl } from "@/components/ImageUpload";
import { Inbox as InboxIcon, MessageSquare } from "lucide-react";

const fmtRelative = (iso) => {
    if (!iso) return "";
    const d = new Date(iso);
    const diff = (Date.now() - d.getTime()) / 1000;
    if (diff < 60) return "just now";
    if (diff < 3600) return `${Math.floor(diff / 60)}m`;
    if (diff < 86400) return `${Math.floor(diff / 3600)}h`;
    if (diff < 604800) return `${Math.floor(diff / 86400)}d`;
    return d.toLocaleDateString(undefined, { month: "short", day: "numeric" });
};

export default function Inbox() {
    const { user } = useAuth();
    const [convs, setConvs] = useState([]);
    const [active, setActive] = useState(null);
    const [loading, setLoading] = useState(true);

    const load = async () => {
        const res = await api.get("/conversations");
        setConvs(res.data);
        setLoading(false);
        if (!active && res.data.length > 0) setActive(res.data[0].id);
    };

    useEffect(() => { load(); /* eslint-disable-next-line */ }, []);
    useEffect(() => {
        const t = setInterval(load, 10000);
        return () => clearInterval(t);
        /* eslint-disable-next-line */
    }, [active]);

    return (
        <Shell>
            <div className="max-w-7xl mx-auto px-6 lg:px-10 py-8">
                <div className="flex items-end justify-between flex-wrap gap-3 mb-6">
                    <div>
                        <div className="label-base">{user?.role === "host_company" ? "Host" : "Vendor"}</div>
                        <h1 className="font-heading text-3xl md:text-4xl font-extrabold flex items-center gap-3">
                            <InboxIcon className="text-brand-coral" /> Inbox
                        </h1>
                    </div>
                </div>

                <div className="grid lg:grid-cols-12 gap-6 h-[calc(100vh-220px)] min-h-[520px]">
                    {/* Conversation list */}
                    <div className="lg:col-span-4 rounded-2xl border border-brand-line bg-white overflow-hidden flex flex-col" data-testid="inbox-list">
                        <div className="px-4 py-3 border-b border-brand-line text-sm font-bold text-brand-mute">
                            {loading ? "Loading…" : `${convs.length} conversation${convs.length === 1 ? "" : "s"}`}
                        </div>
                        <div className="flex-1 overflow-auto">
                            {convs.length === 0 && !loading && (
                                <div className="p-8 text-center text-brand-mute text-sm">
                                    <MessageSquare className="mx-auto mb-3 text-brand-line" size={32} />
                                    No conversations yet. {user?.role === "host_company"
                                        ? "After a vendor confirms their booth, you'll be able to message them from your event page."
                                        : "Once you confirm your booth at an event, the host can message you here."}
                                </div>
                            )}
                            {convs.map((c) => (
                                <button key={c.id} onClick={() => setActive(c.id)} data-testid={`conv-${c.id}`}
                                    className={`w-full text-left px-4 py-3 border-b border-brand-line transition-colors ${active === c.id ? "bg-brand-mint" : "hover:bg-brand-bone"}`}>
                                    <div className="flex items-start gap-3">
                                        <div className="h-10 w-10 rounded-xl bg-brand-mint flex items-center justify-center overflow-hidden font-heading font-extrabold text-brand-forest shrink-0">
                                            {c.counterparty?.logo_url ? <img src={imageUrl(c.counterparty.logo_url)} alt="" className="w-full h-full object-cover" /> : (c.counterparty?.name || "?").slice(0, 1).toUpperCase()}
                                        </div>
                                        <div className="flex-1 min-w-0">
                                            <div className="flex items-center justify-between gap-2">
                                                <div className="font-heading font-bold text-sm truncate">{c.counterparty?.name}</div>
                                                <div className="text-[10px] text-brand-mute shrink-0">{fmtRelative(c.last_message_at)}</div>
                                            </div>
                                            <div className="text-xs text-brand-mute truncate">{c.event?.name}</div>
                                            <div className="text-xs text-brand-ink/70 truncate mt-1">{c.last_message_preview || <span className="italic text-brand-mute">No messages yet</span>}</div>
                                        </div>
                                        {c.unread_count > 0 && (
                                            <span className="ml-1 h-5 min-w-[20px] px-1.5 rounded-full bg-brand-coral text-white text-[10px] font-bold inline-flex items-center justify-center" data-testid={`unread-${c.id}`}>
                                                {c.unread_count}
                                            </span>
                                        )}
                                    </div>
                                </button>
                            ))}
                        </div>
                    </div>

                    {/* Thread view */}
                    <div className="lg:col-span-8 rounded-2xl border border-brand-line bg-white overflow-hidden flex flex-col" data-testid="inbox-thread">
                        {active ? (
                            <MessageThread conversationId={active} viewerRole={user?.role} onChange={load} />
                        ) : (
                            <div className="flex-1 flex flex-col items-center justify-center text-brand-mute text-sm p-8">
                                <MessageSquare size={36} className="text-brand-line mb-3" />
                                Select a conversation to start reading.
                            </div>
                        )}
                    </div>
                </div>
            </div>
        </Shell>
    );
}
